import * as Yup from 'yup'
import { PlanType } from '@labrav/react-components'
import {
  mandatoryEmailMessasge,
  requiredFieldMessage,
  mandatoryNumberFieldMessage,
  migrationProductRequiredMessage,
  productCategoriesProfessionalServicesMessage,
  websiteValidationCheckMessage,
} from './messagesConstants'
import { urlPatternWithHttps } from './constants'
import { professionalServicesCategoryTest } from './professionalServicesCategory'
import { validateField } from './ValidateFields'
import {
  generalInformationHelperSchema,
  productDimesnsionsHelperSchema,
  termsAndConditionsHelperSchema,
} from './FlyoutProductHelperValidation'
import { isEmpty, filter, get } from 'lodash'
import { FlyoutListingProductType } from '../../types/addEditProduct'
import { freeTrialValidation } from './enableFreeTrialValidation'
import { applyListingTextRestrictions } from './listingTextRestrictions'

export enum ApplicationAccessMethod {
  TESTING_CREDENTIALS = 'TESTING_CREDENTIALS',
  INSTRUCTIONS = 'INSTRUCTIONS',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
}

const {
  shortProductDescription,
  productDescription,
  sku,
  productLogoUrl,
  highlights,
  supportResources,
  resourceUrls,
  searchKeywords,
  title,
  eula,
  allowedAccounts,
} = generalInformationHelperSchema.fields
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { multipleDimensionsAllowed, ...restOfFieldsGeneralInformation } =
  generalInformationHelperSchema.fields

const {
  dimensions,
  additionalDimensions,
  contractDuration,
  dimensionCategoryName,
  additionalDimensionsRequired,
} = productDimesnsionsHelperSchema.fields

export const hexOrRgbColorValidation = Yup.string()
  .required(requiredFieldMessage)
  .test('is-hex-or-rgba', 'Please provide a valid HEX color.', value => {
    if (!value) return true
    if (value.startsWith('rgba')) return true
    return /^#[0-9A-F]{8}$/i.test(value)
  })

export const migrationDetailsSchema = Yup.object({
  products: Yup.array().min(1, migrationProductRequiredMessage),
})

export const buyerFormFieldsSchema = Yup.array().of(
  Yup.object().shape({
    title: Yup.string()
      .trim(requiredFieldMessage)
      .required(requiredFieldMessage)
      .max(72, 'Max 72 characters are allowed'),
    dataType: Yup.string().required(requiredFieldMessage),
    isRequired: Yup.boolean().required(requiredFieldMessage),
    validationType: Yup.string().nullable(),
    minLength: Yup.number()
      .typeError('Minimum character length must be a valid number')
      .min(0, 'Not negative number')
      .test(
        'is-integer',
        'Minimum character length should not have a decimal',
        value => value === undefined || Number.isInteger(value)
      )
      .when('maxLength', (maxLength, schema) =>
        schema.test({
          test: (minLength: number) => minLength <= maxLength,
          message:
            'Minimum length must be smaller than or equal to the maximum length',
        })
      )
      .required(mandatoryNumberFieldMessage)
      .when('isRequired', (isRequired, schema) =>
        schema.test({
          test: (minLength: number) =>
            isRequired ? Number(minLength) > 0 : minLength >= 0,
          message: 'Minimum length must be greater than 0 if it is required',
        })
      ),
    maxLength: Yup.number()
      .typeError('Maximum character length must be a valid number')
      .min(0, 'Not negative number')
      .max(500, 'Maximum character length should be less then or equal to 500')
      .required(mandatoryNumberFieldMessage)
      .test(
        'is-integer',
        'Maximum character length should not have a decimal',
        value => value === undefined || Number.isInteger(value)
      ),
  })
)

export const infoCollectionFormMetaDataSchema = Yup.object({
  companyName: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(72, 'Max 72 characters are allowed'),
  logoUrl: Yup.string().required('Please provide a logo'),
  redirectUrl: Yup.string().matches(
    urlPatternWithHttps,
    websiteValidationCheckMessage
  ),
  redirectSeconds: Yup.number()
    .typeError('Redirect seconds must be a valid number')
    .min(0, 'Not negative number'),
  submissionSuccessMessage: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(2000, 'Max 2000 characters are allowed'),
  headingMessage: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(2000, 'Max 2000 characters are allowed'),
  privacyPolicyLink: Yup.string().matches(
    urlPatternWithHttps,
    websiteValidationCheckMessage
  ),
  emailCustomMessage: Yup.string()
    .trim(requiredFieldMessage)
    .max(2000, 'Max 2000 characters are allowed'),
  existingBuyerMessage: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(2000, 'Max 2000 characters are allowed'),
  existingBuyerRedirectSeconds: Yup.number()
    .typeError('Redirect seconds must be a valid number')
    .min(0, 'Not negative number'),
  buyerFormFields: buyerFormFieldsSchema,
  redirectionSettings: Yup.object({
    isPublicFacingSignupPageAvailable: Yup.mixed()
      .nullable(true)
      .test(
        'is-boolean-or-string',
        '1 option is Required',
        value => typeof value === 'boolean' || typeof value === 'string'
      )
      .required('1 option is required'),
    applicationAccessMethod: Yup.string(),
    testingCredentials: Yup.object().shape({
      email: Yup.string()
        .email('Please provide a valid test account email')
        .test(
          'validateTestingCredentialsEmail',
          'Please provide a valid test account email',
          (value, context) => {
            const redirectionSettings =
              context.options.context?.infoCollectionFormMetaData
                ?.redirectionSettings
            if (
              !redirectionSettings?.isPublicFacingSignupPageAvailable &&
              redirectionSettings?.applicationAccessMethod ===
                ApplicationAccessMethod.TESTING_CREDENTIALS
            ) {
              if (isEmpty(value)) {
                return false
              }
            }
            return true
          }
        ),
      password: Yup.string().test(
        'validateTestingCredentialsPassword',
        'Please provide a valid test account password',
        (value, context) => {
          const redirectionSettings =
            context.options.context?.infoCollectionFormMetaData
              ?.redirectionSettings
          if (
            !redirectionSettings?.isPublicFacingSignupPageAvailable &&
            redirectionSettings?.applicationAccessMethod ===
              ApplicationAccessMethod.TESTING_CREDENTIALS
          ) {
            if (isEmpty(value)) {
              return false
            }
          }
          return true
        }
      ),
    }),
    customInstructions: Yup.string()
      .max(1000, 'Max 1000 characters are allowed')
      .trim(requiredFieldMessage)
      .when(['isPublicFacingSignupPageAvailable', 'applicationAccessMethod'], {
        is: (
          isPublicFacingSignupPageAvailable: boolean,
          applicationAccessMethod: ApplicationAccessMethod
        ) => {
          if (
            !isPublicFacingSignupPageAvailable &&
            (applicationAccessMethod ===
              ApplicationAccessMethod.TESTING_CREDENTIALS ||
              applicationAccessMethod === ApplicationAccessMethod.INSTRUCTIONS)
          ) {
            return true
          }
          return false
        },

        then: schema =>
          schema.required('Please provide valid custom instructions'),
      }),
    applicationLoginPageUrl: Yup.string()
      .url(websiteValidationCheckMessage)
      .when(['isPublicFacingSignupPageAvailable', 'applicationAccessMethod'], {
        is: (
          isPublicFacingSignupPageAvailable: boolean,
          applicationAccessMethod: ApplicationAccessMethod
        ) => {
          if (
            !isPublicFacingSignupPageAvailable &&
            (applicationAccessMethod === ApplicationAccessMethod.INSTRUCTIONS ||
              applicationAccessMethod === ApplicationAccessMethod.VIDEO ||
              applicationAccessMethod === ApplicationAccessMethod.DOCUMENT)
          ) {
            return false
          }
          return true
        },

        then: schema =>
          schema.required('Please provide a valid application login page URL'),
      }),
    demoVideoUrl: Yup.string()
      .url(websiteValidationCheckMessage)
      .when(['isPublicFacingSignupPageAvailable', 'applicationAccessMethod'], {
        is: (
          isPublicFacingSignupPageAvailable: boolean,
          applicationAccessMethod: ApplicationAccessMethod
        ) => {
          if (
            !isPublicFacingSignupPageAvailable &&
            applicationAccessMethod === ApplicationAccessMethod.VIDEO
          ) {
            return true
          }
          return false
        },

        then: schema =>
          schema.required('Please provide a valid demo product video URL'),
      }),
    demoDocumentUrl: Yup.string()
      .url(websiteValidationCheckMessage)
      .when(['isPublicFacingSignupPageAvailable', 'applicationAccessMethod'], {
        is: (
          isPublicFacingSignupPageAvailable: boolean,
          applicationAccessMethod: ApplicationAccessMethod
        ) => {
          if (
            !isPublicFacingSignupPageAvailable &&
            applicationAccessMethod === ApplicationAccessMethod.DOCUMENT
          ) {
            return true
          }
          return false
        },

        then: schema =>
          schema.required('Please provide a valid onboarding document URL'),
      }),
    sendDemoToBuyer: Yup.boolean().when(
      ['isPublicFacingSignupPageAvailable', 'applicationAccessMethod'],
      {
        is: (
          isPublicFacingSignupPageAvailable: boolean,
          applicationAccessMethod: ApplicationAccessMethod
        ) => {
          if (
            !isPublicFacingSignupPageAvailable &&
            (applicationAccessMethod === ApplicationAccessMethod.VIDEO ||
              applicationAccessMethod === ApplicationAccessMethod.DOCUMENT)
          ) {
            return true
          }
          return false
        },

        then: Yup.boolean().oneOf([true], 'Please select the checkbox'),
      }
    ),
  }),
  bgColor: hexOrRgbColorValidation,
  buttonColor: hexOrRgbColorValidation,
  buttonTextColor: hexOrRgbColorValidation,
})

export const productDetailsSchema = Yup.object({
  productType: Yup.string().when('listingType', {
    is: FlyoutListingProductType.MIGRATED,
    then: Yup.string(),
    otherwise: Yup.string().required('Product type is required'),
  }),
  productName: applyListingTextRestrictions(
    Yup.string().required(requiredFieldMessage),
    'plain'
  ).max(72, 'Max 72 characters are allowed'),
})

export const generalInformationSchema = Yup.object().when('productDetails', {
  is: (value: any) => {
    return value.productType === PlanType.PROFESSIONAL_SERVICES
  },
  then: Yup.object({
    productCategories: Yup.array()
      .min(1, 'At least 1 category is required')
      .max(3, 'Maximum 3 categories allowed')
      .test({
        name: 'professional-service-categories',
        test: categories => professionalServicesCategoryTest(categories),
        message: productCategoriesProfessionalServicesMessage,
      }),
    shortProductDescription,
    productDescription,
    sku,
    productLogoUrl,
    highlights,
    resourceUrls,
    supportResources,
    searchKeywords,
    title,
    eula,
    allowedAccounts,
  }).test(
    'test-productName',
    'Product name is required.',
    validateField<undefined>('productDetails.productName', undefined)
  ),
  otherwise: generalInformationHelperSchema,
})
declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = get(item, propertyPath)

      if (
        propertyValue &&
        filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})
export const productDimensionsSchema = Yup.object()
  .when('productDetails', {
    is: (value: any) => {
      return value.productType === PlanType.PROFESSIONAL_SERVICES
    },
    then: Yup.object({
      dimensions: Yup.array()
        .of(
          Yup.object().shape({
            label: applyListingTextRestrictions(
              Yup.string().strict(true).required(requiredFieldMessage),
              'plain'
            ).max(500, 'Max 500 characters are allowed'),
            labelDescription: applyListingTextRestrictions(
              Yup.string().required(requiredFieldMessage),
              'plain'
            ).max(1000, 'Max 1000 characters are allowed'),
          })
        )
        .uniqueProperty('label', 'Display name must be unique'),
    }),
  })
  .when('productDetails', {
    is: (value: any) => value.productType === PlanType.SAAS_SUBSCRIPTION,
    then: Yup.object({
      dimensionCategoryName,
      additionalDimensions: additionalDimensionsRequired,
    }),
  })
  .when('productDetails', {
    is: (value: any) => value.productType === PlanType.SAAS_CONTRACT,
    then: Yup.object({
      dimensionCategoryName,
      contractDuration,
      dimensions,
      additionalDimensions,
      freeTrial: freeTrialValidation,
    }),
  })

const emailArrayValidation = Yup.string()
  .email('Enter valid emails')
  .required(mandatoryEmailMessasge)
export const notificationRecipientsSchema = Yup.object({
  registrationCompleted: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  privateOfferRenewalReminder: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  registrationIncompleteOffer: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  subscribeFail: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  entitlementRenew: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  entitlementExpired: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  entitlementUpgrade: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  unsubscribePending: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  unsubscribeSuccess: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  privateOfferExpired: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  privateOfferAccepted: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  privateOfferCreated: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  privateOfferExpiringSoon: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsPrivateOfferRestrictionSuccess: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  meteringSent: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsProductListingLive: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsProductListingRejected: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  freeTrialActivated: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  freeTrialDeactivated: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsResellerAgreementCreated: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsResellerAgreementDeactivated: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsResellerAgreementExpiringSeller: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
  awsResellerAgreementExpiredSeller: Yup.array()
    .of(emailArrayValidation)
    .min(1, mandatoryEmailMessasge)
    .required(mandatoryEmailMessasge),
})

const FlyoutProductValidationSchema = Yup.object().shape({
  generalInformation: generalInformationSchema,
  productDetails: productDetailsSchema,
  productDimensions: productDimensionsSchema,
  infoCollectionFormMetaData: infoCollectionFormMetaDataSchema,
  notificationRecipients: notificationRecipientsSchema,
  migrationDetails: migrationDetailsSchema,
  buyerFormFields: buyerFormFieldsSchema,
  termsAndConditions: termsAndConditionsHelperSchema,
})

export default FlyoutProductValidationSchema
